@layer token {
  :root {
    /* BASE ----------------------------------------------------------------- */

    --n-tooltip-animation-slide-distance: 2px;
    --n-tooltip-animation-duration: 0.15s;
    --n-tooltip-animation-timing-function: ease-out;
    --n-tooltip-animation-fill-mode: forwards;
    --n-tooltip-initial-scale: 0.95;

    --n-tooltip-border-radius: var(--n-border-radius-200);
    --n-tooltip-box-shadow: var(--n-shadow-200);
  }
}

@layer component {
  /* BASE ------------------------------------------------------------------- */

  @keyframes tooltipSlideIn {
    0% {
      opacity: 0;
    }
    1% {
      opacity: 0;
      transform: scale(var(--n-tooltip-initial-scale))
        translate(var(--direction-x), var(--direction-y));
    }
    100% {
      opacity: 1;
      transform: scale(1) translate(0, 0);
    }
  }

  .tooltipEl {
    /* Reset here so a surface nested in another arrowed one does not inherit
       its inset. Only a side arrow needs one: a surface is capped in height
       only when it has no arrow (see `floatingSurface.css`). */
    --n-floating-arrow-inset-inline: 0px;
    overflow: visible;
    animation-name: tooltipSlideIn;
    animation-duration: var(--n-tooltip-animation-duration);
    animation-timing-function: var(--n-tooltip-animation-timing-function);
    animation-fill-mode: var(--n-tooltip-animation-fill-mode);
    opacity: 0;
    transform: scale(1) translate(0, 0);
    position: absolute;

    &[data-placement='top'] {
      --direction-x: 0;
      --direction-y: var(--n-tooltip-animation-slide-distance);
      &[data-has-arrow='true'] {
        padding-bottom: var(--n-floating-content-arrow-base-to-tip-length);
      }
    }

    &[data-placement='top'][data-align='start'] {
      transform-origin: left center;
    }
    &[data-placement='top'][data-align='center'] {
      transform-origin: bottom center;
    }
    &[data-placement='top'][data-align='end'] {
      transform-origin: right center;
    }

    &[data-placement='left'] {
      --direction-x: var(--n-tooltip-animation-slide-distance);
      --direction-y: 0;
      &[data-has-arrow='true'] {
        --n-floating-arrow-inset-inline: var(
          --n-floating-content-arrow-base-to-tip-length
        );
        padding-right: var(--n-floating-content-arrow-base-to-tip-length);
      }
    }

    &[data-placement='left'][data-align='start'] {
      transform-origin: right top;
    }
    &[data-placement='left'][data-align='center'] {
      transform-origin: right center;
    }
    &[data-placement='left'][data-align='end'] {
      transform-origin: right bottom;
    }

    &[data-placement='bottom'] {
      --direction-x: 0;
      --direction-y: calc(var(--n-tooltip-animation-slide-distance) * -1);
      transform-origin: top center;
      &[data-has-arrow='true'] {
        padding-top: var(--n-floating-content-arrow-base-to-tip-length);
      }
    }

    &[data-placement='bottom'][data-align='start'] {
      transform-origin: left center;
    }
    &[data-placement='bottom'][data-align='center'] {
      transform-origin: top center;
    }
    &[data-placement='bottom'][data-align='end'] {
      transform-origin: right center;
    }

    &[data-placement='right'] {
      --direction-x: calc(var(--n-tooltip-animation-slide-distance) * -1);
      --direction-y: 0;
      &[data-has-arrow='true'] {
        --n-floating-arrow-inset-inline: var(
          --n-floating-content-arrow-base-to-tip-length
        );
        padding-left: var(--n-floating-content-arrow-base-to-tip-length);
      }
    }

    &[data-placement='right'][data-align='start'] {
      transform-origin: left top;
    }
    &[data-placement='right'][data-align='center'] {
      transform-origin: left center;
    }
    &[data-placement='right'][data-align='end'] {
      transform-origin: left bottom;
    }
  }

  .tooltip {
    position: relative;
    display: flex;
    border-radius: var(--n-tooltip-border-radius);
    box-shadow: var(--n-tooltip-box-shadow);
  }

  /* The positioner caps and scrolls the root whatever it holds. A lone child
     scrolls itself instead, inside its own border and rounded corners, and the
     root stays unclipped around it: the wrapper takes the root's cap, and as a
     flex item the child stretches to the clamped line (Flexbox §9.4 step 8).
     Only a lone one: beside a sibling, the root has to be the scroller or the
     sibling runs off the viewport, and two would nest. A child that cannot
     shrink, such as one with its own `height`, still spills out of the capped
     wrapper; the positioner reads that as `data-overflowing='true'` on the
     root, which lifts these rules so the root scrolls the whole wrapper,
     shadow included. The flag is absent while the positioner measures, so
     they are in force for that read. Arrowed surfaces are not capped at all
     (see `floatingSurface.css`). The child combinators keep a nested surface
     out of it. */
  .tooltipEl[data-has-arrow='false']:not([data-overflowing='true'])
    > .tooltip:has(> :only-child) {
    max-height: inherit;
  }

  /* Without these a plain box would not shrink below its content, so it could
     neither take the cap nor show the positioner that it overflows. */
  .tooltipEl[data-has-arrow='false']:not([data-overflowing='true'])
    > .tooltip
    > :only-child {
    min-height: 0;
    min-width: 0;
  }

  /* A scroll container clips what its children paint past its padding box,
     and a child with nothing at its edges, such as a button flush in its
     corner, would lose that button's focus ring. So only once the positioner
     has measured the child overflowing its capped box. `FloatingContent` is
     always a scroller instead: its own padding keeps rings clear of the edge,
     and it is where content grows while the surface is open (filter rows
     added after open). The surface re-measures that growth only a frame
     later, so a measured gate would let those rows paint past its border
     until then. */
  .tooltipEl[data-has-arrow='false']:not([data-overflowing='true'])
    > .tooltip
    > :only-child:is([data-overflowing='true'], [data-floating-content]) {
    overflow: auto;
  }
}
